import type {
  EntitlementRedemptionResponse,
  PendingEntitlement,
  PendingEntitlementsResponse,
} from '@/reward-types'
import fetcher from '@/services/fetcher'
import { filterRedeemableEntitlements } from '@/utils/entitlement-utils'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { defineStore } from 'pinia'

type EntitlementTargetType = 'instant_win'

type EntitlementsStoreState = {
  pendingEntitlements: Array<PendingEntitlement>
  isLoading: boolean
  isRedeeming: boolean
  error: string | null
  lastFetchRequestId: number
}

function resolveErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

export const useEntitlementsStore = defineStore('entitlements', {
  state: (): EntitlementsStoreState => ({
    pendingEntitlements: [],
    isLoading: false,
    isRedeeming: false,
    error: null,
    lastFetchRequestId: 0,
  }),

  getters: {
    nextEntitlement(): PendingEntitlement | null {
      return this.pendingEntitlements[0] ?? null
    },

    nextRedeemableUnits(): number {
      return this.nextEntitlement?.units ?? 0
    },

    hasPendingEntitlements(): boolean {
      return this.pendingEntitlements.length > 0
    },
  },

  actions: {
    async fetchPendingEntitlements(
      targetType?: EntitlementTargetType,
      targetCode?: string
    ): Promise<Array<PendingEntitlement>> {
      const requestId = ++this.lastFetchRequestId
      const normalizedTargetType = targetType?.trim()
      const normalizedTargetCode = targetCode?.trim()

      this.isLoading = true
      this.error = null

      try {
        const params = new URLSearchParams()
        if (normalizedTargetType) {
          params.set('target_type', normalizedTargetType)
        }
        if (normalizedTargetCode) {
          params.set('target_code', normalizedTargetCode)
        }

        const queryString = params.toString()
        const url = toPortalUrl(
          `entitlements/pending${queryString ? `?${queryString}` : ''}`
        )

        const { data, error } = await fetcher.get<{
          csi_content: PendingEntitlementsResponse
        }>(url)

        if (requestId !== this.lastFetchRequestId) {
          return this.pendingEntitlements
        }

        if (error.value || !data.value) {
          this.error = resolveErrorMessage(
            error.value,
            'Failed to fetch entitlements'
          )
          this.pendingEntitlements = []
          return []
        }

        // The dashboard inventory is intentionally actionable-only; campaign pages keep blocked context visible.
        const entitlements = filterRedeemableEntitlements(
          data.value?.csi_content?.entitlements ?? []
        )
        this.pendingEntitlements = entitlements

        return entitlements
      } catch (e: unknown) {
        console.error('Failed to fetch pending entitlements:', e)
        if (requestId === this.lastFetchRequestId) {
          this.error = resolveErrorMessage(e, 'Failed to fetch entitlements')
          this.pendingEntitlements = []
        }
        return []
      } finally {
        if (requestId === this.lastFetchRequestId) {
          this.isLoading = false
        }
      }
    },

    async redeemEntitlement(
      entitlementId: number
    ): Promise<EntitlementRedemptionResponse | null> {
      this.isRedeeming = true
      this.error = null

      try {
        const { data, error } = await fetcher.post<{
          csi_content: EntitlementRedemptionResponse
        }>(toPortalUrl(`entitlements/${entitlementId}/redeem`), {})

        if (error.value || !data.value) {
          const apiMessage = resolveErrorMessage(
            error.value,
            'An error occurred while redeeming the entitlement.'
          )
          throw new Error(apiMessage)
        }

        const result = data.value.csi_content

        if (!result) {
          throw new Error('No response from redemption.')
        }

        this.pendingEntitlements = this.pendingEntitlements.filter(
          (e) => e.id !== entitlementId
        )

        return result
      } catch (e: unknown) {
        console.error('Failed to redeem entitlement:', e)
        this.error = resolveErrorMessage(e, 'Failed to redeem entitlement')
        return null
      } finally {
        this.isRedeeming = false
      }
    },

    clearEntitlements() {
      this.pendingEntitlements = []
      this.error = null
    },
  },
})
